import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Table, Empty, Tag } from "antd";
import dayjs from "dayjs";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
import { formatUuidT } from "../../../flatbuffers/display";
import { useLazyGetUsersQuery } from "../../../redux/users/userAPI";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// FILTERS
import FilterSearchText from "../../components/filters/FilterSearchText";
// UTILS
import { getTableRowClass, buildFilterString } from "../../../utils/utils";
import { Permission, hasPermission } from "../../../utils/validatePermissions";
// COMPONENTS
import TableColumnStatusUser from "../../components/table/TableColumnStatusUser";
// STATUS LEGEND
import UserStatusLegend from "../../../components/users/UserStatusLegend";
import TableNoDataBox from "../../components/table/TableNoDataBox";
import UsersTableActions from "./UsersTableActions";
// INTERFACES
import { RootState } from "../../../app/store";

const UsersTable = () => {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { theme } = useSelector((state: RootState) => state.home);
  const [t] = useTranslation("global");
  const { Column } = Table;

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [users, setUsers] = useState<any[]>([]);
  const [filterName, setFilterName] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterUserStatus, setFilterUserStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState<any>("");
  const [pagination, setPagination] = useState<any>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
    total: 0,
  });
  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { userRefreshTable, permissionSet } = useSelector((state: RootState) => state.user);
  const { globalOrganizationId, userLogged } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetUsersTable, { data, isLoading, isError, isFetching, error }] =
    useLazyGetUsersQuery();

  // ************************************************ */
  // FUNCTIONS ************************************** */

  const getData = async (page: number, limit: number) => {
    let idOrganization = globalOrganizationId;
    if (userLogged && userLogged.organizationId && userLogged.organizationId === idOrganization) {
      idOrganization = "";
    }
    const searchAndFilterString = buildFilterString({
      filterFirstName: filterName,
      filterMiddleName: filterName,
      filterLastName: filterName,
      filterEmail,
      filterOrganizationFullName: idOrganization,
      filterUserStatus,
    });
    const token = await GETJwtToken();
    triggerGetUsersTable({
      page,
      limit,
      token,
      searchAndFilterString,
    });
  };

  const handleTableChange = async (pagination_: any) => {
    setPagination({
      ...pagination,
      current: pagination_.current,
      pageSize: pagination_.pageSize,
      total: pagination_.total,
    });
    getData(pagination_.current, pagination_.pageSize);
  };

  const refreshTable = async () => {
    setPagination({
      ...pagination,
      current: 1,
      pageSize: 10,
    });
    getData(1, 10);
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    getData(1, 10);
  }, []);

  useEffect(() => {
    if (data && data.data) {
      setPagination({
        ...pagination,
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: data.total,
      });
      setUsers(data.data);
    }
  }, [data]);

  useEffect(() => {
    if (isError) {
      setUsers([]);
      setPagination({
        ...pagination,
        current: 1,
        pageSize: 10,
        total: 0,
      });
    }
  }, [isError]);

  useEffect(() => {
    if (userRefreshTable) {
      setFilterName("");
      setFilterEmail("");
      setFilterUserStatus("");
      refreshTable();
    }
  }, [userRefreshTable]);

  useEffect(() => {
    if (error) {
      setErrorMessage(error);
      setUsers([]);
      setPagination({
        current: 1,
        pageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50", "100"],
        total: 0,
      });
    }
  }, [error]);

  useEffect(() => {
    if (globalOrganizationId !== "") {
      refreshTable();
    }
  }, [globalOrganizationId]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className="mt-3">
      <>
        {hasPermission(Permission.UsersView, permissionSet ?? []) ? (
          <Table
            locale={{
              emptyText: (
                <TableNoDataBox
                  isError={isError}
                  errorMessage={
                    errorMessage && errorMessage.data && errorMessage.data.message
                      ? errorMessage.data.message
                      : "Error"
                  }
                  noItemsFound="No users found"
                />
              ),
            }}
            rowClassName={(record, index) => getTableRowClass(index, theme)}
            className="my-custom-table"
            dataSource={users}
            loading={isLoading || isFetching}
            rowKey={(record: any) => formatUuidT(record.id)}
            size="small"
            pagination={pagination}
            onChange={handleTableChange}
            scroll={{ x: 1050 }}
            footer={() => (
              <div className="generalStyles__flexEnd">
                <Tag>{pagination.total} Users</Tag>
              </div>
            )}
          >
            {/** NAME */}
            <Column
              title={t("userManagement.name")}
              dataIndex="first_name"
              key="first_name"
              width="200px"
              filterIcon={<FontAwesomeIcon icon={faSearch} />}
              filterDropdown={() => (
                <FilterSearchText
                  placeHolder={t("general.name")}
                  filterValue={filterName}
                  setFilterValue={setFilterName}
                  refreshTable={refreshTable}
                  refreshParameters={[]}
                />
              )}
              render={(_text, record: any) => (
                <div className="generalStyles__ellipsis__200px">
                  {record.firstName || ""} {record.middleName || ""} {record.lastName || ""}
                </div>
              )}
            />
            {/** EMAIL */}
            <Column
              title={t("userManagement.email")}
              dataIndex="email"
              key="email"
              filterIcon={<FontAwesomeIcon icon={faSearch} />}
              filterDropdown={() => (
                <FilterSearchText
                  placeHolder={t("userManagement.email")}
                  filterValue={filterEmail}
                  setFilterValue={setFilterEmail}
                  refreshTable={refreshTable}
                  refreshParameters={[]}
                />
              )}
              render={(text) => <div>{text || "--"}</div>}
            />
            {/** ORGANIZATION */}
            <Column
              title={t("organizationManagement.organization")}
              dataIndex="orgName"
              key="orgName"
              render={(text) => <div>{text || "--"}</div>}
            />
            {/** ROLE */}
            <Column
              title={t("userManagement.role")}
              dataIndex="roleName"
              key="roleName"
              render={(text) => <div>{text || "--"}</div>}
            />
            {/** STATUS */}
            <Column
              align="center"
              title={t("userManagement.status")}
              dataIndex="status"
              key="status"
              width="130px"
              render={(text) => <TableColumnStatusUser text={text} />}
            />
            {/** CREATED AT */}
            <Column
              title={t("general.createdAt")}
              dataIndex="createdAt"
              key="createdAt"
              width="180px"
              render={(text: string | Uint8Array | null) => {
                const value = String(text ?? "").trim();
                if (!value) {
                  return <Tag>--</Tag>;
                }

                const formatted = dayjs(value).isValid()
                  ? dayjs(value).format("YYYY-MM-DD HH:mm")
                  : value;

                return <Tag color="blue">{formatted}</Tag>;
              }}
            />
            {/** ACTIONS */}
            <Column
              title={t("userManagement.actions")}
              key="action"
              width="100px"
              render={(_: any, record: any) => <UsersTableActions selectedItem={record} />}
            />
          </Table>
        ) : (
          <>
            <div className="generalStyles__noAccessToListTable">
              <Empty
                description={t("userManagement.listNotAvailable")}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          </>
        )}
      </>
      {/** ********************************************* */}
      {/** STATUS LEGEND — the same component, and the same colours, the
           wizard's Users step shows, so one status never means two things.
           It used to appear only in the wizard. */}
      <UserStatusLegend />
    </div>
  );
};

export default UsersTable;
